/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {PackagesItemByPerson, PackagesItemsPerson} from "types/entities/PackagesItems";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import BookingAction from "store/booking/BookingAction";
import {PackageCategory} from "types/enums/PackageCategory";
import {UserIcon} from "@heroicons/react/24/solid";
import clsx from "clsx";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import BookingPackageItemsWithKarting from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsWithKarting";
import BookingPackageItemsDurations from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsDurations";
import {currencyNumberFormat} from "utils/utility";
import {PersonType} from "types/enums/PersonType";
import {AVAILABLE_DURATIONS_BASIC} from "helpers/config";

enum HandlerType {
    Duration = "duration",
    Karting = "karting",
}

interface BookingPackagesNewActionDriverRowProps {
    index: number;
    i: number;
    personType: PackageItemPersonType;
    id?: number | null;
    category?: PackageCategory;
}

const BookingPackagesPricing = (props: {category: PackageCategory | undefined; personType: PackageItemPersonType; i: number; selectedPackageItem: PackagesItemsPerson|null}) => {
    if (props.category === PackageCategory.FerienPass && props.personType == PackageItemPersonType.Adult && props.i == 0) {
        return <span>{currencyNumberFormat(0)}</span>;
    }
    if (props.selectedPackageItem && props.selectedPackageItem.price) {
        return <span>{currencyNumberFormat(props.selectedPackageItem.price)}</span>;
    }
    return <span>---</span>;
};

const BookingPackagesNewActionDriverRow = (props: BookingPackagesNewActionDriverRowProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const settings = useTypedSelector(state => state.settingsState);
    const [isKartingDisabled, setIsKartingDisabled] = useState<boolean>(!!settings?.config?.disable_karting_tickets || false);
    const [withKarting, setWithKarting] = useState<number | undefined>(isKartingDisabled ? 0 : 1);
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(240);
    const [selectedPackageItem, setSelectedPackageItem] = useState<PackagesItemsPerson | null>(null);
    const [kartingIsNotAvailable, setKartingIsNotAvailable] = useState<boolean>(false);
    const {getPackageItemForCombination, setPackageItemHandler, getSavedPackagesItemByPerson} = usePackageItemKartingOptions();

    const isAnyPackageItemWithKartingStateAvailable = (newKartingState?: number | undefined) => {
        for (const duration of AVAILABLE_DURATIONS_BASIC) {
            const packageItem = getPackageItemForCombination(duration, newKartingState === undefined ? true : newKartingState === 1, props.personType);
            if (packageItem) {
                return true;
            }
        }
        return false;
    }

    const isPackageItemAvailableDurationInKartingAndWithoutKartingType = (newDuration: number) => {
        const itemWithKarting = getPackageItemForCombination(newDuration, true, props.personType);
        const itemWithoutKarting = getPackageItemForCombination(newDuration, false, props.personType);
        return !!(itemWithKarting && itemWithoutKarting);
    }

    const findClosestPackageItemDuration = (newDuration: number | undefined, newKartingState: number) => {
        let availableDurations = AVAILABLE_DURATIONS_BASIC;
        if (newDuration !== undefined && availableDurations.includes(newDuration)) {
            availableDurations = [
                newDuration,
                ...availableDurations.filter((duration) => duration !== newDuration),
            ];
        }

        for (const duration of availableDurations) {
            const packageItem = getPackageItemForCombination(duration, newKartingState == 1, props.personType);
            if (packageItem) {
                return duration;
            }
        }

        return undefined;
    }

    const getAllAvailablePackageItems = (): PackagesItemsPerson[] => {
        return Object.values(availablePackageItems?.packageItems || {}).flat();
    }

    const findClosestPackageItemForAllPersonTypes = (newDuration: number | undefined, newKartingState: number) => {
        const allAvailablePackageItems = getAllAvailablePackageItems();
        const isKarting = newKartingState === 1;
        let availableDurations = AVAILABLE_DURATIONS_BASIC;

        if (newDuration !== undefined && availableDurations.includes(newDuration)) {
            availableDurations = [
                newDuration,
                ...availableDurations.filter((duration) => duration !== newDuration),
            ];
        }

        for (const duration of availableDurations) {
            const packageItemForAdult = allAvailablePackageItems.find(
                item => item.duration === duration
                && item.karting === isKarting
                && item.person_type === PackageItemPersonType.Adult
            );
            const packageItemForChild = allAvailablePackageItems.find(
                item => item.duration === duration
                && item.karting === isKarting
                && item.person_type === PackageItemPersonType.Child
            );
            if (packageItemForAdult && packageItemForChild) {
                return {
                    [PersonType.Adult]: {
                        id: packageItemForAdult.id
                    },
                    [PersonType.Child]: {
                        id: packageItemForChild.id
                    },
                };
            }
        }

        return undefined;
    }

    const handleChangeAll = async (changeValue: HandlerType, newValue: number) => {
        // change duration
        if (changeValue === HandlerType.Duration) {
            const isAllowChangeDuration = isPackageItemAvailableDurationInKartingAndWithoutKartingType(newValue);

            // there is no ticket available in karting and without karting, so karting will change to the opposite status
            if (!isAllowChangeDuration) {
                const allAvailablePackageItems = getAllAvailablePackageItems();
                const newSelectedPackageItems = selectedPackageItems.map(item => {
                    // if this situation occurs that a change is not allowed (!isAllowChangeDuration), it means that I always set without karting, because they will always be
                    const newTicket: PackagesItemsPerson | undefined = allAvailablePackageItems.find(
                        availableItem => availableItem.duration === newValue && availableItem.person_type === item.person_type && !availableItem.karting
                    );

                    return {
                        personIndex: item.personIndex,
                        id: newTicket ? newTicket.id : null,
                        person_type: item.person_type,
                        quantity: item.quantity,
                    };
                });

                await dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
                return;
            }

            // set new duration with same karting state for all persons
            const newSelectedPackageItems: PackagesItemByPerson[] = selectedPackageItems.map(item => {
                const oldPackageItem: PackagesItemsPerson | undefined = availablePackageItems?.packageItems
                    ? availablePackageItems.packageItems[item.person_type].find(
                          (availablePackageItem: PackagesItemsPerson) => availablePackageItem.id === item.id
                      )
                    : undefined;
                const isCurrentItem = props.index === item.personIndex;
                const packageItem = getPackageItemForCombination(
                    newValue,
                    isCurrentItem ? withKarting == 1 : oldPackageItem ? oldPackageItem.karting : true,
                    item.person_type
                );

                return {
                    personIndex: item.personIndex,
                    id: packageItem ? packageItem.id : null,
                    person_type: item.person_type,
                    quantity: item.quantity,
                };
            });

            await dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
            return;
        }

        // change karting
        if (changeValue === HandlerType.Karting) {
            const isKartingAvailable = isAnyPackageItemWithKartingStateAvailable();
            const kartingState = changeValue === HandlerType.Karting ? newValue : withKarting;
            const currentIsKarting = isKartingDisabled
                ? 0
                : isKartingAvailable && kartingState == 1 ? 1 : 0;
            const packageItem = getPackageItemForCombination(chosenDuration, currentIsKarting == 1, props.personType);

            // set if is duration with same karting
            if (packageItem) {
                setWithKarting(currentIsKarting);
                setPackageItemHandler(props.index, packageItem.id, props.personType, 1);
                return;
            } else {
                // set different duration with same karting
                const closestPackageItem = findClosestPackageItemForAllPersonTypes(chosenDuration, currentIsKarting);
                if (closestPackageItem) {
                    const newSelectedPackageItems = selectedPackageItems.map(item => {
                        return {
                            personIndex: item.personIndex,
                            id: closestPackageItem?.[item.person_type] ? closestPackageItem?.[item.person_type].id : null,
                            person_type: item.person_type,
                            quantity: item.quantity,
                        };
                    });

                    await dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
                    return;
                } else {
                    // set closest duration with different karting
                    const closestPackageItemsWithReverseKarting = findClosestPackageItemForAllPersonTypes(chosenDuration, currentIsKarting === 1 ? 0 : 1);
                    const newSelectedPackageItems = selectedPackageItems.map(item => {
                        return {
                            personIndex: item.personIndex,
                            id: closestPackageItemsWithReverseKarting?.[item.person_type] ? closestPackageItemsWithReverseKarting?.[item.person_type].id : null,
                            person_type: item.person_type,
                            quantity: item.quantity,
                        };
                    });

                    await dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
                    return;
                }
            }
        }
    };

    const setDefaultPackageItems = () => {
        const isKartingAvailable = isAnyPackageItemWithKartingStateAvailable();
        setKartingIsNotAvailable(!isKartingAvailable);
        const currentIsKarting = isKartingDisabled ? 0 : isKartingAvailable && withKarting == 1 ? 1 : 0;
        const closestPackageItemDuration = findClosestPackageItemDuration(chosenDuration, currentIsKarting);

        if (closestPackageItemDuration) {
            const newPackageItem = getPackageItemForCombination(closestPackageItemDuration, currentIsKarting == 1, props.personType);
            if (newPackageItem) {
                setWithKarting(currentIsKarting);
                setChosenDuration(closestPackageItemDuration);
                setSelectedPackageItem(newPackageItem);

                const packageItemSelection = {
                    personIndex: props.index,
                    id: newPackageItem ? newPackageItem.id : null,
                    person_type: newPackageItem.person_type,
                    quantity: 1,
                };
                dispatch(BookingAction.setSelectedPackageItem(packageItemSelection));
            }
        }
    }

    useEffect(() => {
        setIsKartingDisabled(!!settings?.config?.disable_karting_tickets || false);
    }, [settings?.config?.disable_karting_tickets]);

    useEffect(() => {
        if (!availablePackageItems) {
            return;
        }

        if (props?.id) {
            const newPackageItem: PackagesItemsPerson | undefined = availablePackageItems?.packageItems?.[props.personType]
                ? availablePackageItems.packageItems[props.personType].find((availablePackageItem: PackagesItemsPerson) => availablePackageItem.id === props.id)
                : undefined;

            if (newPackageItem) {
                setChosenDuration(newPackageItem.duration);
                setWithKarting(newPackageItem.karting ? 1 : 0);
                setSelectedPackageItem(newPackageItem);
            } else {
                setDefaultPackageItems();
            }
        } else {
            setDefaultPackageItems();
        }
    }, [props?.id, availablePackageItems]);

    return (
        <tr
            className={
                "BookingPackagesNewActionDriverRow flex flex-col lg:table-row last-of-type:border-b-0 border-b lg:border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0"
            }
        >
            <td
                className={
                    "align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell border-b border-dashed sm:border-b-0"
                }
            >
                <div className={"flex flex-row items-center justify-start mb-2 sm:mb-0"}>
                    <UserIcon
                        className={clsx(
                            "h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2",
                            props.personType === PackageItemPersonType.Adult ? "text-teal-500" : "text-pink-400"
                        )}
                    />
                    <div className={"flex flex-col items-center"}>
                        <span className={"font-bold text-sm xl:text-base"}>
                            {props.i + 1}. {t("person")}
                        </span>
                        {bookingProcess.filter &&
                            (bookingProcess.filter.category === PackageCategory.Action ||
                                bookingProcess.filter.category === PackageCategory.School ||
                                bookingProcess.filter.category === PackageCategory.FerienPass) && (
                                <span className={"text-xs text-gray-500 font-medium text-center"}>{t(props.personType)}</span>
                            )}
                    </div>
                </div>
            </td>
            <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]"}>
                    {t("Options")}
                    <span>:</span>
                </h4>
                <BookingPackageItemsWithKarting
                    className={"justify-start lg:justify-center"}
                    kartingIsNotAvailable={kartingIsNotAvailable}
                    setWithKarting={e => handleChangeAll(HandlerType.Karting, e)}
                    withKarting={withKarting}
                />
            </td>
            <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]"}>
                    {t("Ticket by hours")}
                    <span>:</span>
                </h4>
                <BookingPackageItemsDurations
                    disabled1Hour={true}
                    disabled2Hours={false}
                    chosenDuration={chosenDuration}
                    className={"justify-start lg:justify-center"}
                    enabled={withKarting !== undefined}
                    personType={props.personType}
                    setChosenDuration={e => handleChangeAll(HandlerType.Duration, e)}
                    withKarting={withKarting}
                />
            </td>
            <td className={"align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]"}>
                    {t("Price")}
                    <span>:</span>
                </h4>
                <div
                    className={clsx(
                        "flex flex-row items-center justify-center font-medium text-gray-700 text-sm xl:text-base",
                        selectedPackageItem?.price && "underline underline-offset-4 lg:underline-offset-8"
                    )}
                >
                    <BookingPackagesPricing
                        category={props.category}
                        personType={props.personType}
                        i={props.i}
                        selectedPackageItem={selectedPackageItem}
                    />
                </div>
            </td>
        </tr>
    );
};

export default BookingPackagesNewActionDriverRow;
